import Basket from "assets/img/svg/basket";
import Plus from "assets/img/svg/plus";
import type { Action } from "components/shared/ButtonMenu";
import ButtonMenu from "components/shared/ButtonMenu";
import ContractsCounterBlock from "components/shared/ContractsCounterBlock";
import ModalBase from "components/shared/ModalBase";
import StyledButton from "components/shared/StyledButton";
import ChannelsRecap from "components/TermRecap/ChannelsRecap";
import { groupChannelFormulas } from "components/TermRecap/formatTypeRecap";
import GroupRecap from "components/TermRecap/GroupRecap";
import type { GroupParams } from "containers/TermFormContainer";
import { getGroupWithChannels } from "containers/TermFormContainer/utils";
import { PortalType } from "contexts/portal.context";
import { formatLocaleDateString, formatNumber } from "helpers/utils";
import { useActiveSignProvider } from "hooks/useActiveSignProvider";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Agreement } from "store/agreementsStore";
import { fetchChannelCompensationGroups } from "store/channelStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { PressTemplateConfiguration, TermTemplate } from "store/termTemplatesStore";
import { fetchTitleFormatTypes } from "store/titleFormatTypeStore";

import { getTemplateChannels } from "../../../TermTemplates/utils";

interface SignatureContext {
    beneficiaryId: number;
    agreements: Agreement[];
    onCreateAgreement: (template: TermTemplate, documentType: "framework_contract" | "avenant") => void;
}

interface ContentProps {
    template: TermTemplate;
    onClose: () => void;
    onEdit?: (template: TermTemplate) => void;
    onDelete?: (template: TermTemplate) => void;
    signatureContext?: SignatureContext;
}

interface ModalProps {
    open: boolean;
    template: TermTemplate | undefined;
    onClose: () => void;
    onEdit?: (template: TermTemplate) => void;
    onDelete?: (template: TermTemplate) => void;
    signatureContext?: SignatureContext;
}

export const buildRecapSubtitle = (t: (k: string) => string, template: TermTemplate | undefined) =>
    template?.beneficiaryId ? t("FrameworkContractRecap.Kicker") : t("FrameworkContractRecap.KickerGeneric");

export const FrameworkContractRecapContent = ({
    template,
    onClose,
    onEdit,
    onDelete,
    signatureContext,
}: ContentProps) => {
    const [t] = useTranslation();
    const portalType = useContext(PortalType);
    const isPress = portalType === "press" || portalType === "audiovisual";
    const dispatch = useAppDispatch();
    const { channels, channelCompensationGroups } = useAppSelector((state) => state.channels);
    const { items: titleFormatTypes, status: titleFormatTypesStatus } = useAppSelector(
        (state) => state.titleFormatTypes,
    );
    const { getCompanySettings } = useCompanySettings();
    const usesCompensation = !!getCompanySettings("uses_interchannel_compensation");
    const usesAgreements = !!getCompanySettings("uses_agreements");
    const activeSignProvider = useActiveSignProvider();

    useEffect(() => {
        if (isPress) return;
        if (usesCompensation) dispatch(fetchChannelCompensationGroups());
        if (titleFormatTypesStatus === "idle") dispatch(fetchTitleFormatTypes());
    }, [dispatch, isPress, titleFormatTypesStatus, usesCompensation]);

    const formatTypeNamesById = useMemo(
        () => new Map(titleFormatTypes.map((item) => [item.id, item.name])),
        [titleFormatTypes],
    );
    const channelById = useMemo(() => new Map(channels.map((channel) => [channel.id, channel])), [channels]);

    const templateAgreements = useMemo(() => {
        if (!signatureContext) return [];
        return signatureContext.agreements.filter(
            (a) => a.relatedResource === "frameworkContract" && +a.relatedResourceId === +template.id,
        );
    }, [signatureContext, template.id]);

    const newDocumentMenuActions: Action[] = signatureContext
        ? [
              {
                  name: t("ContractSettings.SendForSignatureViaProvider", { provider: activeSignProvider.label }),
                  action: () => signatureContext.onCreateAgreement(template, "framework_contract"),
              },
              {
                  name: t("TermTemplates.CreateAvenant"),
                  action: () => signatureContext.onCreateAgreement(template, "avenant"),
              },
          ]
        : [];

    const parsedChannels = useMemo(
        () => getTemplateChannels(template.configuration, channels, { usesCompensation }) ?? [],
        [template, channels, usesCompensation],
    );

    const advance = (template.configuration?.advancePayment ?? 0) / 100;
    const compensate = +`${template.configuration?.compensate ?? 0}` === 1;
    const standaloneRows = usesCompensation
        ? parsedChannels.filter((channel) => channel.groupId == null)
        : parsedChannels;
    const standaloneFormulas = groupChannelFormulas(standaloneRows);
    const groupsWithChannels = usesCompensation ? getGroupWithChannels(channelCompensationGroups, parsedChannels) : [];
    const channelsCount = new Set(parsedChannels.map((channel) => channel.channelId)).size;

    const pressConfig = template.configuration as unknown as PressTemplateConfiguration;
    const pressFolioPrice = (pressConfig?.folioPrice ?? 0) / 100;

    // Dates are stored as "yyyy-MM-dd" calendar-day strings (see form). formatLocaleDateString
    // with timeZone: "UTC" preserves the stored day for users in any timezone.
    const startDateRaw = template.configuration?.startDate;
    const endDateRaw = template.configuration?.endDate;
    const hasValidityPeriod = !!(startDateRaw || endDateRaw);
    const formatDay = (raw: string) =>
        formatLocaleDateString({ date: raw as unknown as Date, options: { numeric: true, timeZone: "UTC" } });

    return (
        <div style={{ display: "flex", flexDirection: "column" }}>
            {/* Body (scrollable) */}
            <div style={{ padding: "20px 24px 4px", overflowY: "auto", maxHeight: "60vh" }}>
                {usesAgreements && signatureContext && (
                    <div style={{ marginBottom: "20px" }}>
                        <ContractsCounterBlock
                            agreements={templateAgreements}
                            resourceType="frameworkContract"
                            resourceId={template.id}
                            parentId={signatureContext.beneficiaryId}
                            primaryAction={
                                <ButtonMenu
                                    mainActions={{
                                        icon: <Plus />,
                                        name: t("ContractSettings.NewDocumentForSignature"),
                                    }}
                                    actions={newDocumentMenuActions}
                                    showExpandBtn={false}
                                    fullWidth
                                />
                            }
                        />
                    </div>
                )}
                <div
                    style={{
                        fontSize: "11px",
                        color: "#8C8C92",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        fontWeight: 600,
                        margin: "0 0 12px",
                    }}
                >
                    {t("FrameworkContractRecap.Configuration")}
                </div>
                {isPress ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "20px" }}>
                        <div style={{ border: "1px solid #EFEFEF", borderRadius: "8px", padding: "12px 14px" }}>
                            <div style={{ fontSize: "11px", color: "#8C8C92", marginBottom: "4px" }}>
                                {t("TermTemplates.PressFolioPrice")}
                            </div>
                            <div
                                style={{
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {formatNumber(pressFolioPrice, { isCurrency: true, always2Digit: true })}
                            </div>
                        </div>
                        <div style={{ border: "1px solid #EFEFEF", borderRadius: "8px", padding: "12px 14px" }}>
                            <div style={{ fontSize: "11px", color: "#8C8C92", marginBottom: "4px" }}>
                                {t("TermTemplates.PressWorkNature")}
                            </div>
                            <div style={{ fontSize: "14px", fontWeight: 500 }}>{pressConfig?.workNature || "—"}</div>
                        </div>
                    </div>
                ) : (
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "12px",
                            marginBottom: "20px",
                        }}
                    >
                        <div style={{ border: "1px solid #EFEFEF", borderRadius: "8px", padding: "12px 14px" }}>
                            <div style={{ fontSize: "11px", color: "#8C8C92", marginBottom: "4px" }}>
                                {t("FrameworkContractRecap.Advance")}
                            </div>
                            <div
                                style={{
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {formatNumber(advance, { isCurrency: true, always2Digit: true })}
                            </div>
                        </div>
                        <div style={{ border: "1px solid #EFEFEF", borderRadius: "8px", padding: "12px 14px" }}>
                            <div style={{ fontSize: "11px", color: "#8C8C92", marginBottom: "4px" }}>
                                {t("FrameworkContractRecap.Compensation")}
                            </div>
                            <div style={{ fontSize: "14px", fontWeight: 500 }}>
                                {compensate ? t("ConnectTitle.Yes") : t("ConnectTitle.No")}
                            </div>
                        </div>
                    </div>
                )}

                {hasValidityPeriod && (
                    <>
                        <div
                            style={{
                                fontSize: "11px",
                                color: "#8C8C92",
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                                fontWeight: 600,
                                margin: "0 0 12px",
                            }}
                        >
                            {t("TermTemplates.ValidityPeriod")}
                        </div>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "12px",
                                marginBottom: "20px",
                            }}
                        >
                            <div style={{ border: "1px solid #EFEFEF", borderRadius: "8px", padding: "12px 14px" }}>
                                <div style={{ fontSize: "11px", color: "#8C8C92", marginBottom: "4px" }}>
                                    {t("TermTemplates.StartDate")}
                                </div>
                                <div
                                    style={{
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        fontVariantNumeric: "tabular-nums",
                                    }}
                                >
                                    {startDateRaw ? formatDay(startDateRaw) : "—"}
                                </div>
                            </div>
                            <div style={{ border: "1px solid #EFEFEF", borderRadius: "8px", padding: "12px 14px" }}>
                                <div style={{ fontSize: "11px", color: "#8C8C92", marginBottom: "4px" }}>
                                    {t("TermTemplates.EndDate")}
                                </div>
                                <div
                                    style={{
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        fontVariantNumeric: "tabular-nums",
                                    }}
                                >
                                    {endDateRaw ? formatDay(endDateRaw) : "—"}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {!isPress && (
                    <div
                        style={{
                            fontSize: "11px",
                            color: "#8C8C92",
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                            fontWeight: 600,
                            margin: "0 0 4px",
                        }}
                    >
                        {t("FrameworkContractRecap.Channels", { count: channelsCount })}
                    </div>
                )}
                {!isPress &&
                    [...standaloneFormulas.entries()].map(([channelId, formulas]) => (
                        <ChannelsRecap
                            key={channelId}
                            channel={formulas.defaultRow}
                            formatOverrides={formulas.overrides}
                            fullChannelData={channelById.get(channelId)}
                            formatTypeNamesById={formatTypeNamesById}
                        />
                    ))}
                {!isPress &&
                    groupsWithChannels.map((group: GroupParams) => {
                        const groupChannels = parsedChannels.filter((channel) => channel.groupId === group.id);
                        const defaultChannelIds = new Set(
                            groupChannels
                                .filter((channel) => channel.formatTypeId == null)
                                .map((channel) => channel.channelId),
                        );
                        const nameChannelIds =
                            defaultChannelIds.size > 0
                                ? defaultChannelIds
                                : new Set(groupChannels.map((channel) => channel.channelId));
                        const groupChannelsNames = channels
                            .filter((channel) => nameChannelIds.has(channel.id))
                            .map((channel) => channel.name);

                        return (
                            <GroupRecap
                                key={group.id}
                                group={group}
                                groupChannels={groupChannels}
                                groupChannelsNames={groupChannelsNames}
                                channels={channels}
                                formatTypeNamesById={formatTypeNamesById}
                            />
                        );
                    })}
            </div>

            {/* Footer */}
            <div
                style={{
                    padding: "16px 24px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "8px",
                    borderTop: "1px solid #eaeaea",
                    background: "#FBFBFB",
                }}
            >
                {onDelete ? (
                    <StyledButton buttonType="danger" onClick={() => onDelete(template)}>
                        <Basket />
                        {t("FrameworkContractRecap.Delete")}
                    </StyledButton>
                ) : (
                    <span />
                )}
                <div style={{ display: "flex", gap: "8px" }}>
                    <StyledButton buttonType="secondary" onClick={onClose}>
                        {t("FrameworkContractRecap.Close")}
                    </StyledButton>
                    {onEdit && (
                        <StyledButton buttonType="primary" onClick={() => onEdit(template)}>
                            {t("FrameworkContractRecap.Edit")}
                        </StyledButton>
                    )}
                </div>
            </div>
        </div>
    );
};

const FrameworkContractRecapModal = ({ open, template, onClose, onEdit, onDelete, signatureContext }: ModalProps) => {
    const [t] = useTranslation();
    if (!template) return null;
    return (
        <ModalBase
            open={open}
            onClose={onClose}
            modalTitle={template.name}
            modalSubtitle={buildRecapSubtitle(t, template)}
            widthTransition={{ width: "560px", active: true }}
            maxHeight="90vh"
            contentStyle={{ padding: 0 }}
        >
            <FrameworkContractRecapContent
                template={template}
                onClose={onClose}
                onEdit={onEdit}
                onDelete={onDelete}
                signatureContext={signatureContext}
            />
        </ModalBase>
    );
};

export default FrameworkContractRecapModal;
